import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api/client";

const inputCls =
  "w-full h-9 px-3 rounded-md border border-input bg-background text-sm " +
  "focus:outline-none focus:ring-2 focus:ring-primary-500/40";

/**
 * Đổi mật khẩu người dùng tự làm.
 *
 * Hệ cũ PiOffice có màn này (changePassword.zul) nhưng hệ mới bị bỏ sót: API là
 * bản rỗng trả 404 và web không có giao diện nào. Người dùng nhận tài khoản mới
 * không tự đổi được mật khẩu ban đầu.
 *
 * Sau khi đổi xong BẮT BUỘC đăng xuất: token cũ vẫn còn hiệu lực vì JWT không
 * trạng thái, để nguyên thì người dùng tưởng đã đổi mà phiên cũ vẫn dùng được.
 */
export function ChangePasswordModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [hienTai, setHienTai] = useState("");
  const [moi, setMoi] = useState("");
  const [nhapLai, setNhapLai] = useState("");
  const [hienMatKhau, setHienMatKhau] = useState(false);
  const [loi, setLoi] = useState<string | null>(null);

  const doi = useMutation({
    mutationFn: async () => {
      const res = await api.post("/user/change-password", {
        currentPassword: hienTai,
        newPassword: moi,
      });
      return res.data;
    },
    onSuccess: () => onDone(),
    onError: (e: unknown) => {
      // Máy chủ trả {code, data, msg} với msg là tiếng Việt hiển thị được.
      const ax = e as { response?: { data?: { msg?: string } } };
      setLoi(ax.response?.data?.msg ?? "Không đổi được mật khẩu, thử lại sau");
    },
  });

  function guiDi() {
    setLoi(null);
    if (moi !== nhapLai) {
      setLoi("Mật khẩu mới và ô nhập lại không giống nhau");
      return;
    }
    if (moi.length < 6) {
      setLoi("Mật khẩu mới phải có ít nhất 6 ký tự");
      return;
    }
    if (moi === hienTai) {
      setLoi("Mật khẩu mới phải khác mật khẩu hiện tại");
      return;
    }
    doi.mutate();
  }

  const kieu = hienMatKhau ? "text" : "password";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Đổi mật khẩu"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="md" onClick={onClose} disabled={doi.isPending}>
            Huỷ
          </Button>
          <Button
            variant="default"
            size="md"
            onClick={guiDi}
            disabled={doi.isPending || !hienTai || !moi || !nhapLai}
          >
            {doi.isPending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4 mr-1.5" />
            )}
            {doi.isPending ? "Đang đổi…" : "Đổi mật khẩu"}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium">Mật khẩu hiện tại</label>
          <input
            className={inputCls}
            type={kieu}
            value={hienTai}
            autoFocus
            onChange={(e) => setHienTai(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Mật khẩu mới</label>
          <input
            className={inputCls}
            type={kieu}
            value={moi}
            onChange={(e) => setMoi(e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">Ít nhất 6 ký tự.</p>
        </div>
        <div>
          <label className="text-sm font-medium">Nhập lại mật khẩu mới</label>
          <input
            className={inputCls}
            type={kieu}
            value={nhapLai}
            onChange={(e) => setNhapLai(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && guiDi()}
          />
        </div>

        <button
          type="button"
          onClick={() => setHienMatKhau((s) => !s)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          {hienMatKhau ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {hienMatKhau ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
        </button>

        {loi && (
          <p className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">
            {loi}
          </p>
        )}
      </div>
    </Modal>
  );
}
